import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eligibleExperiencesQueryOptions, experienceStateQueryOptions, GUIDED_EXPERIENCE_QUERY_KEYS } from '@/api/queries/guided-experiences';
import { myActiveCheckInsQueryOptions } from '@/api/queries/check-ins';
import { friendsQueryOptions } from '@/api/queries/friendship';
import { listsQueryOptions } from '@/api/queries/lists';
import * as guidedExperiencesApi from '@/api/endpoints/guided-experiences';
import { useAuth } from '@/hooks/use-auth';
import type { PaginatedListResponse } from '@/features/lists/types';
import type { OnboardingIntent } from '../types';

const ONBOARDING_EXPERIENCE_ID = 'onboarding';

export interface ChecklistItemDef {
  id: string;
  label: string;
  completed: boolean;
  route: string;
}

export interface UseProgressiveChecklistReturn {
  items: ChecklistItemDef[];
  isVisible: boolean;
  isLoading: boolean;
  dismiss: () => Promise<void>;
}

const EXPERIENCE_ID = 'home_progressive_checklist';

const CHECKLIST_ITEMS: Record<string, { label: string; route: string }> = {
  rate_dish: { label: 'Rate a dish', route: '/(root)/check-in' },
  add_friend: { label: 'Add a friend', route: '/(tabs)/discover' },
  save_place: { label: 'Save a place to your wishlist', route: '/(root)/lists' },
  check_in: { label: 'Check in somewhere', route: '/(root)/check-in' },
};

const INTENT_ORDER: Record<OnboardingIntent, string[]> = {
  REVIEWER: ['rate_dish', 'add_friend', 'save_place'],
  WISHLIST_BUILDER: ['save_place', 'rate_dish', 'add_friend'],
  TRACKER: ['check_in', 'rate_dish', 'save_place'],
};

const DEFAULT_ORDER = ['rate_dish', 'add_friend', 'save_place'];

export function useProgressiveChecklist(): UseProgressiveChecklistReturn {
  const queryClient = useQueryClient();
  const hasAutoCompletedRef = useRef(false);
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const { data: eligibilityData, isLoading: isEligibilityLoading } = useQuery(
    eligibleExperiencesQueryOptions(userId)
  );

  const checklistIsEligible = useMemo(() => {
    if (!eligibilityData) return false;
    return eligibilityData.experiences.some((e) => e.experience_id === EXPERIENCE_ID);
  }, [eligibilityData]);

  const { data: onboardingState } = useQuery({
    ...experienceStateQueryOptions(ONBOARDING_EXPERIENCE_ID),
    enabled: checklistIsEligible,
  });

  const { data: checkIns } = useQuery(myActiveCheckInsQueryOptions());
  const { data: friends } = useQuery(friendsQueryOptions());
  const { data: lists } = useQuery(listsQueryOptions());

  const isVisible = checklistIsEligible;

  const intent = useMemo((): OnboardingIntent | undefined => {
    if (!onboardingState?.payload) return undefined;
    try {
      const parsed = JSON.parse(onboardingState.payload);
      return parsed.intent;
    } catch {
      return undefined;
    }
  }, [onboardingState]);

  const completionStatus = useMemo(() => ({
    rate_dish: (checkIns?.total ?? 0) > 0,
    add_friend: Array.isArray(friends) && friends.length > 0,
    save_place: (lists as PaginatedListResponse | undefined)?.items?.some(
      (list) => list.itemCount > 0
    ) ?? false,
    check_in: (checkIns?.total ?? 0) > 0,
  }), [checkIns, friends, lists]);

  const items = useMemo((): ChecklistItemDef[] => {
    const order = intent ? INTENT_ORDER[intent] : DEFAULT_ORDER;
    return order.map((id) => ({
      id,
      label: CHECKLIST_ITEMS[id].label,
      completed: completionStatus[id as keyof typeof completionStatus] ?? false,
      route: CHECKLIST_ITEMS[id].route,
    }));
  }, [intent, completionStatus]);

  const allCompleted = useMemo(
    () => items.length > 0 && items.every((item) => item.completed),
    [items]
  );

  const ensureStarted = useCallback(async () => {
    const checklist = eligibilityData?.experiences.find(
      (e) => e.experience_id === EXPERIENCE_ID
    );
    if (!checklist?.status) {
      await guidedExperiencesApi.startExperience(EXPERIENCE_ID);
    }
  }, [eligibilityData]);

  useEffect(() => {
    if (allCompleted && isVisible && !hasAutoCompletedRef.current) {
      hasAutoCompletedRef.current = true;
      ensureStarted()
        .then(() => guidedExperiencesApi.completeExperience(EXPERIENCE_ID))
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId),
          });
        });
    }
  }, [allCompleted, isVisible, queryClient, ensureStarted, userId]);

  const dismiss = useCallback(async () => {
    await ensureStarted();
    await guidedExperiencesApi.dismissExperience(EXPERIENCE_ID);
    queryClient.invalidateQueries({
      queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId),
    });
  }, [queryClient, ensureStarted, userId]);

  return {
    items,
    isVisible,
    isLoading: isEligibilityLoading,
    dismiss,
  };
}
